import { useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useApp } from "../context";
import { getActiveLearnerId } from "../hooks/useActiveLearner";

// Premium learning tools all live under these route prefixes. The bare "/learn"
// hub is intentionally excluded (it re-locks itself and shows the banner).
const isPremiumToolPath = (p) =>
  p !== "/learn" && (p.startsWith("/learn/") || p.startsWith("/academy/") || p.startsWith("/learning-"));

// Watches the active learner's premium-preview window. The moment the window
// ends, if the learner is NOT a real (paid) premium user and is currently
// inside a premium tool, we send them back to the Learning Forest hub, "logging
// them out" of the tool and reverting them to their normal access level.
export default function PreviewExpiryWatcher() {
  const { user, refreshUser } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const hadPreview = useRef(false);
  const timer = useRef(null);

  const check = useCallback(async () => {
    const learnerId = getActiveLearnerId(user?.id);
    if (!learnerId) return;
    try {
      const { data } = await axios.get(`${API}/entitlements/resolve/${encodeURIComponent(learnerId)}`);
      const isPreview = !!data?.preview;
      const wasPreview = hadPreview.current;

      if (isPreview) {
        if (timer.current) clearTimeout(timer.current);
        const exp = data?.preview_expires_at;
        if (exp) {
          const ms = new Date(exp).getTime() - Date.now();
          if (ms > 0 && ms < 2 ** 31 - 1) timer.current = setTimeout(check, ms + 1500);
        }
      } else if (wasPreview && !data?.is_premium) {
        // The preview just ended and they have no paid premium.
        try { window.dispatchEvent(new CustomEvent("iw:preview-ended")); } catch (_e) { /* ignore */ }
        if (isPremiumToolPath(window.location.pathname)) {
          toast("Your premium preview has ended. You're back to your usual access.", { duration: 6000 });
          navigate("/learn");
        }
        if (refreshUser) refreshUser();
      }
      hadPreview.current = isPreview;
    } catch {
      /* ignore */
    }
  }, [user?.id, navigate, refreshUser]);

  // Re-check on mount, on every route change, and on a slow poll (catches
  // windows that close without a precomputed expiry, e.g. recurring windows).
  useEffect(() => {
    check();
    const iv = setInterval(check, 45000);
    return () => {
      clearInterval(iv);
      if (timer.current) clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [check, location.pathname]);

  return null;
}
